import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';

export type PageMode = 'list' | 'form' | 'view';

/**
 * Simple catalog route prefixes — entities without child tables.
 * These display as popup-style centered dialog on desktop.
 */
export const SIMPLE_CATALOG_PREFIXES = [
    // Tech tools
    '/molds', '/plates',
    '/layouts', '/technical-specs',
    // System entities
    '/machines', '/processes', '/roles', '/users',
    // Warehouse
    '/fg-inventory', '/material-warehouse',
    // Factory
    '/machine-logs',
];

/**
 * Detect page type from the current pathname.
 * - 'form'  → /create or /edit pages
 * - 'view'  → /{resource}/{id} detail pages (and /print)
 * - 'list'  → everything else (list pages, dashboard, etc.)
 */
export function usePageMode(): PageMode {
    const location = useLocation();
    const pathname = location.pathname;

    return useMemo(() => {
        // Remove trailing slash for consistency
        const p = pathname.replace(/\/$/, '') || '/';

        // Form pages: end with /create or /edit
        if (p.endsWith('/create') || p.endsWith('/edit')) {
            return 'form';
        }

        // Known list-only paths (no param segment)
        const listOnlyPaths = [
            '/', '/analytics', '/configs', '/workflows', '/company-info',
            '/guide', '/changelog', '/project-brief', '/live-erd',
            '/my-requests',
            '/terminal', '/wip-transfer', '/machine-status', '/factory-queue',
            '/process-planning', '/factory/process-planning',
            '/material-planning', '/planning/paper-allocation',
            '/material-inventory-report', '/fg-inventory-report',
            '/factory/production-logs', '/factory/machine-logs', '/factory/packing',
            '/plates/ctp-logs', '/production/ctp-tracking',
            '/production/prepress', '/production/print-layouts', '/master/tooling',
            '/mes/oee', '/mes/maintenance', '/mes/andon', '/mes/incidents', '/shopfloor/terminal',
            '/warehouse/fg/transactions', '/warehouse/material/transactions',
            '/warehouse/master-data', '/inventory/transactions', '/warehouse/inventory-nxt',
            '/warehouse/stocktake', '/warehouse/fg', '/warehouse/tech', '/warehouse/material',
            '/finance/receipts', '/finance/payments',
            '/sales/debts', '/finance/ar', '/finance/ar-aging', '/purchasing/debts',
            '/finance/ap', '/finance/reconciliations', '/finance/ar/reconciliations', '/finance/ap/reconciliations',
            '/qc/ncr', '/qc/check', '/qc/final', '/qc/templates', '/qc/material', '/qc/process',
            '/qc/reports', '/quality/reports', '/quality/materials',
            '/reports/sales', '/reports/production', '/reports/inventory', '/reports/ar-aging',
            '/admin/config-master', '/system/audit-logs', '/system/user-events',
        ];

        if (listOnlyPaths.includes(p)) {
            return 'list';
        }

        // Trang tài liệu một cấp: Hồ sơ cá nhân, Cài đặt người dùng — nút thao tác lên thanh tiêu đề (04/11/2026)
        if (p === '/profile' || p === '/settings' || p === '/settings/account') {
            return 'view';
        }

        // Split path into segments
        const segments = p.split('/').filter(Boolean);

        // Single segment = list page (e.g., /orders, /customers)
        if (segments.length <= 1) {
            return 'list';
        }

        // Two or more segments: check if last segment looks like an ID or param
        // View pages: /resource/id or /resource/id/print
        const lastSegment = segments[segments.length - 1];
        if (lastSegment === 'print') {
            return 'view';
        }

        // If the path has 2+ segments and isn't create/edit, it's a view
        // e.g., /orders/ORD001, /customers/C001, /qc-materials/QC001
        return 'view';
    }, [pathname]);
}

/**
 * Returns true if the current route is a simple catalog (no child tables).
 * Used to apply popup-style layout on desktop.
 */
export function useIsSimpleCatalog(): boolean {
    const location = useLocation();
    const pathname = location.pathname;

    return useMemo(() => {
        const p = pathname.replace(/\/$/, '') || '/';
        return SIMPLE_CATALOG_PREFIXES.some(prefix => p.startsWith(prefix + '/'));
    }, [pathname]);
}
